import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getHotComics } from '@/mocks/comics';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(id) : undefined;
  const relatedComics = getHotComics(4);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 韩国漫画大全 | 免费在线韩漫阅读`;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [comic]);

  if (!comic) {
    return (
      <main>
        <div className="px-4 md:px-6 py-20">
          <div className="max-w-7xl mx-auto text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-6">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-block whitespace-nowrap px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const formatViews = (views: number): string => {
    if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author
    },
    description: comic.description,
    genre: comic.category,
    datePublished: comic.chapters[0]?.updateTime || '',
    url: typeof window !== 'undefined' ? window.location.href : '',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画大全'
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${comic.category}韩漫`,
        item: typeof window !== 'undefined' ? `${window.location.origin}/category/${comic.categorySlug}` : `/category/${comic.categorySlug}`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
        item: typeof window !== 'undefined' ? window.location.href : ''
      }
    ]
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main>
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { name: comic.category, path: `/category/${comic.categorySlug}` },
                  { name: comic.title }
                ]}
              />
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="w-full lg:w-72 shrink-0">
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 sticky top-24">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画大全免费在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    comic.status === '连载'
                      ? 'bg-accent-100 text-accent-900'
                      : 'bg-secondary-100 text-secondary-900'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-user-line mr-1"></i>{comic.author}
                  </span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-eye-line mr-1"></i>{formatViews(comic.views)}
                  </span>
                  <span className="text-sm text-foreground-600">
                    <i className="ri-star-fill text-amber-400 mr-1"></i>{comic.rating} 分
                  </span>
                </div>

                <div className="mt-6 p-5 rounded-lg bg-background-100">
                  <h2 className="text-sm font-semibold text-foreground-950 mb-3">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="whitespace-nowrap px-4 py-2 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-price-tag-3-line mr-1"></i>{comic.category}
                  </Link>
                  <button className="whitespace-nowrap px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer">
                    <i className="ri-bookmark-line mr-1"></i>加入收藏
                  </button>
                  <button className="whitespace-nowrap px-4 py-2 rounded-full bg-background-200 text-foreground-700 text-xs font-medium hover:bg-background-300 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line mr-1"></i>分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-100">
          <div className="px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                章节列表
                <span className="text-sm font-normal text-foreground-500 ml-2">共 {comic.chapters.length} 话</span>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {comic.chapters.map((ch) => (
                  <div
                    key={ch.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors truncate">
                      {ch.title}
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{ch.updateTime}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {relatedComics.filter(c => c.id !== comic.id).slice(0, 4).map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}